import React, { useState, useMemo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import './Gallery.css';
import { getCategories } from '../firebase/dataService';

function Gallery({ images, profile, onImageClick, initialCategory }) {
  const location = useLocation();
  const [selectedCategory, setSelectedCategory] = useState(initialCategory || 'الكل');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const cachedCats = localStorage.getItem('site_categories');
  const [categories, setCategories] = useState(
    cachedCats ? JSON.parse(cachedCats) : ['الكل']
  );
  const [showAllCategories, setShowAllCategories] = useState(false);
  const imagesPerPage = 20;

  // تحميل التصنيفات من Firebase
  useEffect(() => {
    loadCategories();
  }, []);

  // تحديث التصنيف المختار عند تغيير initialCategory
  useEffect(() => {
    if (initialCategory) {
      setSelectedCategory(initialCategory);
    }
  }, [initialCategory]);

  // استقبال البحث من الهيدر
  useEffect(() => {
    if (location.state?.headerSearchTerm) {
      setSearchQuery(location.state.headerSearchTerm);
      setSelectedCategory('الكل');
      setCurrentPage(1);
      window.history.replaceState({}, '');
    }
  }, [location.state]);

  const loadCategories = async () => {
    try {
      const cats = await getCategories();
      const full = ['الكل', ...cats];
      setCategories(full);
      localStorage.setItem('site_categories', JSON.stringify(full));
    } catch (error) {
      console.error('Error loading categories:', error);
      // في حالة الخطأ، استخدم التصنيفات الافتراضية
      setCategories([
        'الكل',
        'الفقه',
        'العقيدة',
        'الحديث',
        'التفسير وعلوم القرآن',
        'السيرة النبوية',
        'السير والتراجم',
        'الرقائق',
        'الآداب والأخلاق',
        'الوسواس القهري',
        'وصايا علمية',
        'متفرقات'
      ]);
    }
  };

  const filteredImages = useMemo(() => {
    let filtered = images;
    
    // تصفية حسب الفئة
    if (selectedCategory !== 'الكل') {
      filtered = filtered.filter(image => image.category === selectedCategory);
    }
    
    // تصفية حسب البحث
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(image => {
        const caption = (image.caption || '').toLowerCase();
        const extractedText = (image.extractedText || '').toLowerCase();
        return caption.includes(query) || extractedText.includes(query);
      });
    }
    
    return filtered;
  }, [images, selectedCategory, searchQuery]);

  // حساب الصفحات
  const totalPages = Math.ceil(filteredImages.length / imagesPerPage);
  const indexOfLastImage = currentPage * imagesPerPage;
  const indexOfFirstImage = indexOfLastImage - imagesPerPage;
  const currentImages = filteredImages.slice(indexOfFirstImage, indexOfLastImage);

  // إعادة تعيين الصفحة عند تغيير الفلتر
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory, searchQuery]);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleImageClick = (image) => {
    if (onImageClick) {
      // تمرير الصورة والتصنيف المختار حالياً
      onImageClick(image, selectedCategory);
    }
  };

  return (
    <div className="gallery">
        <div className="gallery-container">
        <div className={`category-filters ${showAllCategories ? 'expanded' : ''}`}>
          {(showAllCategories ? categories : categories.filter(c => !c.includes('التفسير')).slice(0, 5)).map((category, index) => (
            <button
              key={index}
              className={`category-btn ${selectedCategory === category ? 'active' : ''}`}
              onClick={() => setSelectedCategory(category)}
            >
              {category.startsWith('العقيدة') && category.includes('البدع') ? 'العقيدة' : category}
            </button>
          ))}
          {categories.length > 5 && (
            <button
              className="category-btn more-btn"
              onClick={() => setShowAllCategories(!showAllCategories)}
            >
              {showAllCategories ? 'إخفاء ▲' : 'المزيد ▼'}
            </button>
          )}
        </div>
        
        <div className="gallery-info">
          {selectedCategory && (
            <div className="category-header-wrapper">
              <h2 className="category-title">
                {selectedCategory === 'الكل' ? 'كل التصنيفات' : selectedCategory}
              </h2>
              <div className="category-divider"></div>
              <p className="results-info">
                عرض {indexOfFirstImage + 1} - {Math.min(indexOfLastImage, filteredImages.length)} من {filteredImages.length}
              </p>
            </div>
          )}
        </div>

        <div className="gallery-grid">
          {currentImages.map((image, index) => (
            <div
              key={index}
              className="gallery-item"
              onClick={() => handleImageClick(image)}
            >
              <img
                src={image.url}
                alt={image.caption || `صورة ${index + 1}`}
                className="gallery-image"
                loading="lazy"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = '/tweet-icon.jpg';
                  e.target.style.objectFit = 'contain';
                }}
              />
              <div className="gallery-overlay">
                <div className="overlay-content">
                  <svg className="overlay-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                  </svg>
                  <span className="overlay-text">عرض الموضوع</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {totalPages > 1 && (
          <div className="pagination">
            <button
              className="pagination-btn"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              السابق →
            </button>
            
            <div className="pagination-numbers">
              {[...Array(totalPages)].map((_, index) => {
                const pageNumber = index + 1;
                // عرض الصفحات القريبة فقط
                if (
                  pageNumber === 1 ||
                  pageNumber === totalPages ||
                  (pageNumber >= currentPage - 2 && pageNumber <= currentPage + 2)
                ) {
                  return (
                    <button
                      key={pageNumber}
                      className={`pagination-number ${currentPage === pageNumber ? 'active' : ''}`}
                      onClick={() => handlePageChange(pageNumber)}
                    >
                      {pageNumber}
                    </button>
                  );
                } else if (
                  pageNumber === currentPage - 3 ||
                  pageNumber === currentPage + 3
                ) {
                  return <span key={pageNumber} className="pagination-dots">...</span>;
                }
                return null;
              })}
            </div>
            
            <button
              className="pagination-btn"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              ← التالي
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default Gallery;
